import { supabase } from '@/integrations/supabase/client';

export type DashboardUserType = 'client' | 'practitioner';

export interface UnreadSystemMessage {
  id: string;
  title?: string;
  body?: string;
  created_at?: string;
  target_type: 'all' | 'all_clients' | 'all_practitioners' | 'specific_user';
  target_user_id?: string | null;
  target_user_email?: string | null;
  expires_at?: string | null;
  is_active?: boolean | null;
}

export const SYSTEM_UNREAD_COUNT_QUERY_DESCRIPTION =
  "system_messages.select(id,title,body,created_at,target_type,target_user_id,target_user_email,is_active,expires_at).eq(is_active,true).or(expires_at.is.null,expires_at.gt.nowIso); system_message_reads.select(message_id).eq(user_id); system_message_dismissals.select(message_id).eq(user_id); unread = activeRelevant - reads - dismissals";

const isRelevantSystemMessage = (
  message: UnreadSystemMessage,
  userId: string,
  userEmail: string | undefined,
  userType: DashboardUserType,
) => {
  return (
    message.target_type === 'all' ||
    (message.target_type === 'all_clients' && userType === 'client') ||
    (message.target_type === 'all_practitioners' && userType === 'practitioner') ||
    (message.target_type === 'specific_user' &&
      (message.target_user_id === userId || message.target_user_email === userEmail))
  );
};

export const fetchUnreadSystemMessages = async ({
  userId,
  userEmail,
  userType,
  limit = 1000,
}: {
  userId: string;
  userEmail?: string;
  userType: DashboardUserType;
  limit?: number;
}) => {
  const nowIso = new Date().toISOString();

  const [dismissalsResult, readsResult, messagesResult] = await Promise.all([
    supabase
      .from('system_message_dismissals')
      .select('message_id')
      .eq('user_id', userId),
    supabase
      .from('system_message_reads')
      .select('message_id')
      .eq('user_id', userId),
    supabase
      .from('system_messages')
      .select('id, title, body, created_at, target_type, target_user_id, target_user_email, expires_at, is_active')
      .eq('is_active', true)
      .or(`expires_at.is.null,expires_at.gt.${nowIso}`)
      .order('created_at', { ascending: false })
      .limit(limit),
  ]);

  if (dismissalsResult.error || readsResult.error || messagesResult.error) {
    throw new Error(JSON.stringify({
      dismissalsError: dismissalsResult.error,
      readsError: readsResult.error,
      messagesError: messagesResult.error,
    }));
  }

  const dismissedIds = new Set((dismissalsResult.data || []).map((d: any) => d.message_id as string));
  const readIds = new Set((readsResult.data || []).map((r: any) => r.message_id as string));
  const activeRelevantMessages = ((messagesResult.data || []) as UnreadSystemMessage[])
    .filter((message) => isRelevantSystemMessage(message, userId, userEmail, userType));
  const unreadMessages = activeRelevantMessages
    .filter((message) => !readIds.has(message.id) && !dismissedIds.has(message.id));

  const activeMessageIds = new Set(activeRelevantMessages.map((message) => message.id));
  const activeReadCount = Array.from(readIds).filter((id) => activeMessageIds.has(id)).length;
  const activeDismissedCount = Array.from(dismissedIds).filter((id) => activeMessageIds.has(id)).length;

  return {
    query: SYSTEM_UNREAD_COUNT_QUERY_DESCRIPTION,
    readIds,
    dismissedIds,
    activeRelevantMessages,
    unreadMessages,
    debug: {
      totalActiveMessages: activeRelevantMessages.length,
      readCount: activeReadCount,
      dismissedCount: activeDismissedCount,
      finalUnreadCount: unreadMessages.length,
    },
  };
};